/**
 * Lime Token Utility
 * Extracts authenticated user information from Loom navigation
 */

/**
 * Retrieves the authenticated user's alias from the Loom identity provider
 * @async
 * @function getAuthenticatedUser
 * @returns {Promise<string>} The user's Midway alias
 * @throws {Error} If user identity or Midway provider info cannot be retrieved
 */
export async function getAuthenticatedUser() {
    try {
        const userIdentity = window.loom.getUserIdentity();
        const identities = userIdentity.identities;

        const getUserAlias = (identities) => {
            const userInfo = identities.find((identity) => identity.providerName === 'MIDWAY');
            if (!userInfo) {
                throw new Error('MIDWAY identity not found in user identities');
            }
            return userInfo.userId;
        };

        return getUserAlias(identities);
    } catch (error) {
        console.error('Failed to get authenticated user from Loom:', error);
        throw new Error('Unable to retrieve user identity. Please ensure you are properly authenticated.');
    }
}

/**
 * Retrieves the authenticated user's email address
 * For Midway users, constructs email using fixed amazon.com domain
 * @async
 * @function getAuthenticatedUserEmail
 * @returns {Promise<string>} The user's email address
 * @throws {Error} If user identity cannot be retrieved or user is not a Midway user
 */
export async function getAuthenticatedUserEmail() {
    try {
        const userIdentity = window.loom.getUserIdentity();
        const identities = userIdentity.identities;

        const getUserEmail = (identities) => {
            const userInfo = identities.find((identity) => identity.providerName === 'MIDWAY');
            if (!userInfo) {
                throw new Error('MIDWAY identity not found in user identities');
            }
            return `${userInfo.userId}@amazon.com`;
        };

        return getUserEmail(identities);
    } catch (error) {
        console.error('Failed to get user email:', error);
        throw new Error('Unable to retrieve user email. Please ensure you are properly authenticated.');
    }
}

/**
 * Retrieves the full user identity object from Loom
 * @async
 * @function getUserIdentity
 * @returns {Promise<Object>} The complete user identity object
 * @throws {Error} If user identity cannot be retrieved
 */
export async function getUserIdentity() {
    try {
        return window.loom.getUserIdentity();
    } catch (error) {
        console.error('Failed to get user identity from Loom:', error);
        throw new Error('Unable to retrieve user identity. Please ensure you are properly authenticated.');
    }
}
